import Vue from 'vue';
import VueRouter from 'vue-router';

import store from '@core/store';

import categorisationExpired from './middleware/categorisationExpired';
import checkBrowserCompatibility from './middleware/checkBrowserCompatibility';
import checkTermsConditions from './middleware/checkTermsConditions';
import checkUrlForContext from './middleware/checkUrlForContext';
import clearQueues from './middleware/clearQueues';
import closeAfterSuccessfulLogin from './middleware/closeAfterSuccessfulLogin';
import discoverLocale from './middleware/discoverLocale';
import features from './middleware/features';
import loadAccount from './middleware/loadAccount';
import loadBranding from './middleware/loadBranding';
import loadOrganisation from './middleware/loadOrganisation';
import loadRegistration from './middleware/loadRegistration';
import loadUser from './middleware/loadUser';
import preload from './middleware/preload';
import redirectIncompleteInvestor from './middleware/redirectIncompleteInvestor';
import redirectRestrictedInvestor from './middleware/redirectRestrictedInvestor';
import redirectSuspendedOrganisation from './middleware/redirectSuspendedOrganisation';
import redirectRestrictedRoute from './middleware/redirectRestrictedRoute';
import resetQuerySelectors from './middleware/resetQuerySelectors';
import setAdviser from './middleware/setAdviser';
import setFreshdeskScope from './middleware/setFreshdeskScope';
import setInvestor from './middleware/setInvestor';
import setPageTitle from './middleware/setPageTitle';
import setRouterHistory from './middleware/setRouterHistory';
import setSentryScope from './middleware/setSentryScope';
import urlGuard from './middleware/urlGuard';
import routes from './routes';

const { push } = VueRouter.prototype;
const { isNavigationFailure, NavigationFailureType } = VueRouter;

const onRouterError = error => {
    if (isNavigationFailure(error, NavigationFailureType.duplicated)) {
        // console.warn(error);
    } else if (isNavigationFailure(error, NavigationFailureType.redirected)) {
        // console.warn(error);
    } else if (isNavigationFailure(error, NavigationFailureType.aborted)) {
        // console.warn(error);
    } else if (isNavigationFailure(error, NavigationFailureType.cancelled)) {
        // console.warn(error);
    } else {
        throw error;
    }
};

VueRouter.prototype.push = function (location) {
    return push.call(this, location).catch(onRouterError);
};

VueRouter.prototype.replace = function (location) {
    return push.call(this, location).catch(onRouterError);
};

Vue.use(VueRouter);

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    linkExactActiveClass: 'is-active',
    routes
});

router.beforeEach((to, from, next) => {
    store.dispatch('app/startRouting');
    next();
});

// Check the users browser for compatibility
router.beforeEach(checkBrowserCompatibility);

// Set the router history store state;
router.beforeEach(setRouterHistory);

// Clear all snackbar items;
router.beforeEach(clearQueues);

// Close the current window if close-after-successful-login is true
router.beforeEach(closeAfterSuccessfulLogin);

// Set localStorage item when switching accounts in the same organisation;
router.beforeEach(checkUrlForContext);

// Set the current user in the auth store;
router.beforeEach(loadUser);

// Set the current registration in the auth store;
router.beforeEach(loadRegistration);

// Set the organisation in the auth store;
router.beforeEach(loadOrganisation);

// Set the account in the auth store;
router.beforeEach(loadAccount);

// Change context to the organisation/account for the current URL;
router.beforeEach(urlGuard);

// Replace route component based on feature flags
router.beforeEach(features);

// Set Freshdesk scope
router.beforeEach(setFreshdeskScope);

// Set Sentry scope
router.beforeEach(setSentryScope);

// Set the investor in the auth store;
router.beforeEach(setInvestor);

// Set the adviser in the auth store;
router.beforeEach(setAdviser);

// Check the users' terms and conditions are valid
router.beforeEach(checkTermsConditions);

// Redirect a suspended organisation
router.beforeEach(redirectSuspendedOrganisation);

// Check if the user needs to recategorise
router.beforeEach(categorisationExpired);

// Redirect an incomplete investor to their settings
router.beforeEach(redirectIncompleteInvestor);

// Redirect a restricted investor
router.beforeEach(redirectRestrictedInvestor);

// Redirect a user when they don't have access to a route
router.beforeEach(redirectRestrictedRoute);

// Set the branding in the auth store;
router.beforeEach(loadBranding);

// Set the users current locale;
router.beforeEach(discoverLocale);

// Preload data into vuex;
router.beforeEach(preload);

// Set the browser page title;
router.beforeEach(setPageTitle);

// Reset pinned investor
router.beforeEach(resetQuerySelectors);

router.afterEach(to => {
    store.dispatch('app/setBreadcrumbs', []);
    store.dispatch('app/endRouting');
    store.dispatch('app/setCurrentRoute', to);
});

export default router;
